import { useState, useEffect } from 'react';
import { loadFacilityFromStorage } from '../utils/facilityStorage';
import { getAuthToken } from '../utils/cookieUtils';

// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

interface Facility {
  id: number;
  name: string;
}

export const useUnreadMessageCount = (refreshInterval: number = 30000) => {
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUnreadCount = async () => {
    const selectedFacility = loadFacilityFromStorage();
    
    if (!selectedFacility) {
      setUnreadCount(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const authToken = getAuthToken();

      // Use the same GraphQL query from DriverCommsWidget but only get count
      const threadsQuery = `
        query {
          unreadThreadsByFacilityId(
            facilityId: "${selectedFacility.id}"
            sinceHours: 24
          ) {
            threads {
              uuid
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: threadsQuery
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      const count = result.data.unreadThreadsByFacilityId?.threads?.length || 0;
      setUnreadCount(count);
    } catch (err) {
      console.error('Failed to fetch unread message count:', err);
      setError('Failed to load unread message count');
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch immediately
    fetchUnreadCount();

    // Set up interval to refresh count
    const interval = setInterval(fetchUnreadCount, refreshInterval);

    return () => clearInterval(interval);
  }, [refreshInterval]);

  // Listen for facility changes in localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'selectedFacility') {
        fetchUnreadCount();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return { unreadCount, loading, error, refetch: fetchUnreadCount };
};
